// Tax ID validation patterns and functions
// Based on Stripe docs: https://docs.stripe.com/billing/customer/tax-ids
// and Wikipedia:      https://en.wikipedia.org/wiki/VAT_identification_number

// ----------------------------------------------------------------------------
// 1) PATTERN DEFINITIONS
// ----------------------------------------------------------------------------

export const TAX_ID_VALIDATION_PATTERNS = {
  // TIER 1: STRICT (government-verified on Stripe side)
  eu_vat: {
    validationLevel: 'strict',
    patterns: {
      AT: /^ATU\d{8}$/,
      BE: /^BE[01]\d{9}$/,
      BG: /^BG\d{9,10}$/,
      HR: /^HR\d{11}$/,
      CY: /^CY\d{8}[A-Z]$/,
      CZ: /^CZ\d{8,10}$/,
      DK: /^DK\d{8}$/,
      EE: /^EE\d{9}$/,
      FI: /^FI\d{8}$/,
      FR: /^FR[A-HJ-NP-Z0-9]{2}\d{9}$/,
      DE: /^DE\d{9}$/,
      GR: /^(EL|GR)\d{9}$/,
      HU: /^HU\d{8}$/,
      IE: /^IE(\d{7}[A-W]|\d[A-W]\d{5}[A-W]|\d{7}[A-W]{2})$/,
      IT: /^IT\d{11}$/,
      LV: /^LV\d{11}$/,
      LT: /^LT(\d{9}|\d{12})$/,
      LU: /^LU\d{8}$/,
      MT: /^MT\d{8}$/,
      NL: /^NL\d{9}B\d{2}$/,
      PL: /^PL\d{10}$/,
      PT: /^PT\d{9}$/,
      RO: /^RO\d{2,10}$/,
      SK: /^SK\d{10}$/,
      SI: /^SI\d{8}$/,
      ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
      SE: /^SE\d{12}$/,
    },
    genericPattern: /^[A-Z]{2}[A-Z0-9]{4,15}$/,
    message: 'Enter a valid EU VAT number format.',
    strictMessage:
      'Invalid EU VAT format. This will be verified against government databases.',
  },

  gb_vat: {
    validationLevel: 'strict',
    patterns: {
      GB: /^GB(\d{9}(\d{3})?|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    },
    genericPattern: /^GB(\d{9}(\d{3})?|GD[0-4]\d{2}|HA[5-9]\d{2})$/,
    message: 'Enter a valid UK VAT number format.',
    strictMessage:
      'Invalid UK VAT format. This will be verified against HMRC database.',
  },

  au_abn: {
    validationLevel: 'strict',
    patterns: { AU: /^\d{11}$/ },
    genericPattern: /^\d{11}$/,
    message: 'Enter an 11-digit Australian ABN.',
    strictMessage:
      'Invalid ABN format. This will be verified against Australian Business Register.',
  },
  au_arn: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a 12-digit Australian ARN.',
  },

  // TIER 2: INFORMATIONAL (basic client-side guidance)
  us_ein: {
    validationLevel: 'informational',
    genericPattern: /^\d{2}-?\d{7}$/,
    message: 'Enter a US EIN (format: 12-3456789)',
  },
  ca_bn: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a 9-digit Canadian BN',
  },
  ca_gst_hst: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}RT\d{4}$/i,
    message: 'Enter a Canadian GST/HST (format: 123456789RT0002)',
  },
  ca_pst_bc: {
    validationLevel: 'informational',
    genericPattern: /^PST-?\d{4}-?\d{4}$/i,
    message: 'Enter a BC PST number (format: PST-1234-5678)',
  },
  ca_pst_mb: {
    validationLevel: 'informational',
    genericPattern: /^\d{6}-?\d$/,
    message: 'Enter a Manitoba PST number (format: 123456-7)',
  },
  ca_pst_sk: {
    validationLevel: 'informational',
    genericPattern: /^\d{7}$/,
    message: 'Enter a Saskatchewan PST number (format: 1234567)',
  },
  ca_qst: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}TQ\d{4}$/i,
    message: 'Enter a Quebec QST number (format: 1234567890TQ1234)',
  },
  ch_vat: {
    validationLevel: 'informational',
    genericPattern: /^CHE-?\d{3}\.?\d{3}\.?\d{3}\s?(MWST|TVA|IVA)$/i,
    message: 'Enter a Swiss VAT number (format: CHE-123.456.789 MWST)',
  },
  ch_uid: {
    validationLevel: 'informational',
    genericPattern: /^CHE-?\d{3}\.?\d{3}\.?\d{3}\s?HR$/i,
    message: 'Enter a Swiss UID (format: CHE-123.456.789 HR)',
  },
  no_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}MVA$/i,
    message: 'Enter a Norway VAT (format: 123456789MVA)',
  },
  no_voec: {
    validationLevel: 'informational',
    genericPattern: /^\d{7}$/,
    message: 'Enter a 7-digit Norway VOEC number',
  },
  is_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{5,6}$/,
    message: 'Enter a 5-6 digit Iceland VAT number',
  },
  jp_cn: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a 13-digit Japan Corporate Number',
  },
  jp_rn: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a 12-digit Japan Registration Number',
  },
  jp_trn: {
    validationLevel: 'informational',
    genericPattern: /^T\d{13}$/i,
    message: 'Enter a Japan TRN (format: T1234567890123)',
  },
  kr_brn: {
    validationLevel: 'informational',
    genericPattern: /^\d{3}-?\d{2}-?\d{5}$/,
    message: 'Enter a Korea BRN (format: 123-45-67890)',
  },
  in_gst: {
    validationLevel: 'informational',
    genericPattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9][Z][0-9]$/i,
    message: 'Enter an India GST (format: 12ABCDE1234F1Z5)',
  },
  sg_gst: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{8}[A-Z]$/i,
    message: 'Enter a Singapore GST (format: M12345678X)',
  },
  sg_uen: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}[A-Z]$/i,
    message: 'Enter a Singapore UEN (format: 123456789F)',
  },

  ro_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{2,10}$/,
    message: 'Enter a Romania TIN (2-10 digits)',
  },

  default: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z0-9-\s]{4,20}$/,
    message: 'Please enter your tax ID',
  },

  al_tin: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{8}[A-Z]$/,
    message: 'Enter a valid Albanian TIN (e.g. J12345678N)',
  },
  ad_nrt: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]-\d{6}-[A-Z]$/,
    message: 'Enter a valid Andorran NRT (e.g. A-123456-Z)',
  },
  ao_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Angolan TIN (10 digits)',
  },
  ar_cuit: {
    validationLevel: 'informational',
    genericPattern: /^\d{2}-?\d{8}-?\d{1}$/,
    message: 'Enter a valid Argentinian CUIT (e.g. 12-3456789-01)',
  },
  am_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Armenian TIN (8 digits)',
  },
  aw_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Aruban TIN (8 digits)',
  },
  az_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Azerbaijani TIN (10 digits)',
  },
  bs_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{3}\.\d{3}\.\d{3}$/,
    message: 'Enter a valid Bahamian TIN (e.g. 123.456.789)',
  },
  bh_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{15}$/,
    message: 'Enter a valid Bahrain VAT (15 digits)',
  },
  bd_bin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}-\d{4}$/,
    message: 'Enter a valid Bangladesh BIN (e.g. 123456789-0123)',
  },
  bb_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a valid Barbados TIN (13 digits)',
  },
  by_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Belarus TIN (9 digits)',
  },
  bj_ifu: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a valid Benin IFU (13 digits)',
  },
  bo_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Bolivian TIN (9 digits)',
  },
  ba_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a valid Bosnia and Herzegovina TIN (12 digits)',
  },
  br_cnpj: {
    validationLevel: 'informational',
    genericPattern: /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/,
    message: 'Enter a valid Brazilian CNPJ (e.g. 01.234.456/5432-10)',
  },
  br_cpf: {
    validationLevel: 'informational',
    genericPattern: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
    message: 'Enter a valid Brazilian CPF (e.g. 123.456.789-87)',
  },
  bg_uic: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Bulgarian UIC (9 digits)',
  },
  bf_ifu: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}[A-Z]$/,
    message: 'Enter a valid Burkina Faso IFU (e.g. 12345678A)',
  },
  kh_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{4}-\d{9}$/,
    message: 'Enter a valid Cambodian TIN (e.g. 1001-123456789)',
  },
  cm_niu: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{9}[A-Z]$/,
    message: 'Enter a valid Cameroon NIU (e.g. M123456789L)',
  },
  cv_nif: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Cape Verde NIF (9 digits)',
  },
  cl_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{2}\.\d{3}\.\d{3}-[0-9K]$/,
    message: 'Enter a valid Chilean TIN (e.g. 12.345.678-K)',
  },
  cn_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{18}$/,
    message: 'Enter a valid Chinese TIN (18 digits)',
  },
  co_nit: {
    validationLevel: 'informational',
    genericPattern: /^\d{3}\.\d{3}\.\d{3}-\d$/,
    message: 'Enter a valid Colombian NIT (e.g. 123.456.789-0)',
  },
  cd_nif: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{7}[A-Z]$/,
    message: 'Enter a valid Congo (DR) NIF (e.g. A0123456M)',
  },
  cr_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d-\d{3}-\d{6}$/,
    message: 'Enter a valid Costa Rica TIN (e.g. 1-234-567890)',
  },
  hr_oib: {
    validationLevel: 'informational',
    genericPattern: /^\d{11}$/,
    message: 'Enter a valid Croatian OIB (11 digits)',
  },
  do_rcn: {
    validationLevel: 'informational',
    genericPattern: /^\d{3}-\d{7}-\d$/,
    message: 'Enter a valid Dominican Republic RCN (e.g. 123-4567890-1)',
  },
  ec_ruc: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a valid Ecuador RUC (13 digits)',
  },
  eg_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Egyptian TIN (9 digits)',
  },
  sv_nit: {
    validationLevel: 'informational',
    genericPattern: /^\d{4}-\d{6}-\d{3}-\d$/,
    message: 'Enter a valid El Salvador NIT (e.g. 1234-567890-123-4)',
  },
  et_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Ethiopian TIN (10 digits)',
  },
  eu_oss_vat: {
    validationLevel: 'informational',
    genericPattern: /^EU\d{9}$/,
    message: 'Enter a valid EU OSS VAT (e.g. EU123456789)',
  },
  ge_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Georgian VAT (9 digits)',
  },
  gn_nif: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Guinea NIF (9 digits)',
  },
  de_stn: {
    validationLevel: 'informational',
    genericPattern: /^\d{11}$/,
    message: 'Enter a valid German Tax Number (11 digits)',
  },
  hk_br: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Hong Kong BR (8 digits)',
  },
  hu_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Hungarian TIN (10 digits)',
  },
  id_npwp: {
    validationLevel: 'informational',
    genericPattern: /^\d{2}\.\d{3}\.\d{3}\.\d-\d{3}\.\d{3}$/,
    message: 'Enter a valid Indonesian NPWP (e.g. 12.123.123.1-123.123)',
  },
  il_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Israeli VAT (9 digits)',
  },
  kz_bin: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a valid Kazakhstan BIN (12 digits)',
  },
  ke_pin: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{9}[A-Z]$/,
    message: 'Enter a valid Kenyan PIN (e.g. A123456789Z)',
  },
  kg_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Kyrgyzstan TIN (9 digits)',
  },
  la_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Laos TIN (9 digits)',
  },
  li_uid: {
    validationLevel: 'informational',
    genericPattern: /^LI\d{6}$/,
    message: 'Enter a valid Liechtenstein UID (e.g. LI123456)',
  },
  li_vat: {
    validationLevel: 'informational',
    genericPattern: /^LI\d{5}$/,
    message: 'Enter a valid Liechtenstein VAT (e.g. LI12345)',
  },
  my_frp: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Malaysian FRP (8 digits)',
  },
  my_itn: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{10}$/,
    message: 'Enter a valid Malaysian ITN (e.g. C1234567890)',
  },
  my_sst: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{2}-\d{4}-\d{8}$/,
    message: 'Enter a valid Malaysian SST (e.g. A12-3456-78912345)',
  },
  mr_nif: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a valid Mauritania NIF (13 digits)',
  },
  mx_rfc: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]{3,4}\d{6}[A-Z0-9]{3}$/,
    message: 'Enter a valid Mexican RFC (e.g. ABC010203AB9)',
  },
  md_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{7}$/,
    message: 'Enter a valid Moldova VAT (7 digits)',
  },
  me_pib: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Montenegro PIB (8 digits)',
  },
  ma_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{15}$/,
    message: 'Enter a valid Moroccan VAT (15 digits)',
  },
  np_pan: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Nepal PAN (9 digits)',
  },
  nz_gst: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid New Zealand GST (9 digits)',
  },
  ng_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}-\d{4}$/,
    message: 'Enter a valid Nigerian TIN (e.g. 12345678-0001)',
  },
  mk_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a valid North Macedonia VAT (13 digits)',
  },
  om_vat: {
    validationLevel: 'informational',
    genericPattern: /^OM\d{10}$/,
    message: 'Enter a valid Oman VAT (e.g. OM1234567890)',
  },
  pe_ruc: {
    validationLevel: 'informational',
    genericPattern: /^\d{11}$/,
    message: 'Enter a valid Peru RUC (11 digits)',
  },
  ph_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a valid Philippines TIN (12 digits)',
  },
  ru_inn: {
    validationLevel: 'informational',
    genericPattern: /^\d{10,12}$/,
    message: 'Enter a valid Russian INN (10 or 12 digits)',
  },
  ru_kpp: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Russian KPP (9 digits)',
  },
  sa_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{15}$/,
    message: 'Enter a valid Saudi Arabia VAT (15 digits)',
  },
  sn_ninea: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}[A-Z]\d$/,
    message: 'Enter a valid Senegal NINEA (e.g. 12345672A2)',
  },
  rs_pib: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Serbian PIB (9 digits)',
  },
  si_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Slovenian TIN (8 digits)',
  },
  za_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid South African VAT (10 digits)',
  },
  es_cif: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]\d{8}$/,
    message: 'Enter a valid Spanish CIF (e.g. A12345678)',
  },
  sr_fin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Suriname FIN (10 digits)',
  },
  tw_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}$/,
    message: 'Enter a valid Taiwan VAT (8 digits)',
  },
  tj_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Tajikistan TIN (9 digits)',
  },
  tz_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{8}[A-Z]$/,
    message: 'Enter a valid Tanzania VAT (e.g. 12345678A)',
  },
  th_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{13}$/,
    message: 'Enter a valid Thailand VAT (13 digits)',
  },
  tr_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Turkish TIN (10 digits)',
  },
  ug_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Ugandan TIN (10 digits)',
  },
  ua_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Ukrainian VAT (9 digits)',
  },
  ae_trn: {
    validationLevel: 'informational',
    genericPattern: /^\d{15}$/,
    message: 'Enter a valid UAE TRN (15 digits)',
  },
  uy_ruc: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a valid Uruguay RUC (12 digits)',
  },
  uz_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{9}$/,
    message: 'Enter a valid Uzbekistan TIN (9 digits)',
  },
  uz_vat: {
    validationLevel: 'informational',
    genericPattern: /^\d{12}$/,
    message: 'Enter a valid Uzbekistan VAT (12 digits)',
  },
  ve_rif: {
    validationLevel: 'informational',
    genericPattern: /^[A-Z]-\d{8}-\d$/,
    message: 'Enter a valid Venezuela RIF (e.g. A-12345678-9)',
  },
  vn_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Vietnam TIN (10 digits)',
  },
  zm_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Zambia TIN (10 digits)',
  },
  zw_tin: {
    validationLevel: 'informational',
    genericPattern: /^\d{10}$/,
    message: 'Enter a valid Zimbabwe TIN (10 digits)',
  },
};

// ----------------------------------------------------------------------------
// 2) LENGTH CHECKER (type-aware)
// ----------------------------------------------------------------------------

const validateTaxIdLength = (value, type) => {
  // Only to enforce length for types that we know require it and are not covered by regex.
  return { meetsLengthRequirement: true, lengthWarning: null };
};

// ----------------------------------------------------------------------------
// 3) VALIDATOR FUNCTION
// ----------------------------------------------------------------------------

export const validateTaxId = (value, type) => {
  if (!value || !type) return { isValid: null, message: '' };

  const validation =
    TAX_ID_VALIDATION_PATTERNS[type] || TAX_ID_VALIDATION_PATTERNS['default'];
  const cleanValue = value.toUpperCase().replace(/\s/g, '');
  const lengthCheck = validateTaxIdLength(value, type);

  // Strict: government-verified
  if (validation.validationLevel === 'strict') {
    if (type === 'eu_vat') {
      const cc = cleanValue.slice(0, 2);
      const pat = validation.patterns[cc];
      const ok =
        !!pat && pat.test(cleanValue) && lengthCheck.meetsLengthRequirement;
      return {
        isValid: ok,
        message: ok ? `${cc} VAT format is valid` : validation.strictMessage,
        validationLevel: 'strict',
      };
    }
    // other strict types
    const ok =
      validation.genericPattern.test(cleanValue) &&
      lengthCheck.meetsLengthRequirement;
    return {
      isValid: ok,
      message: ok
        ? 'Tax ID format is valid'
        : validation.strictMessage || validation.message,
      validationLevel: 'strict',
    };
  }

  // Informational
  const formatValid = validation.genericPattern.test(cleanValue);
  const meetsLen = lengthCheck.meetsLengthRequirement;
  let msg = validation.message;
  const isValid = formatValid && meetsLen;
  if (isValid) msg = 'Tax ID format looks good';
  return {
    isValid,
    message: msg,
    disclaimer: null,
    validationLevel: 'informational',
    lengthWarning: null,
  };
};

export const hasBlockingValidationErrors = (result) =>
  result.isValid === false && result.validationLevel === 'strict';

// ----------------------------------------------------------------------------
// 4) SEARCH & SELECT HELPERS
// ----------------------------------------------------------------------------

export const filterTaxIdOptions = (options, searchTerm) => {
  if (!searchTerm) return options;
  const term = searchTerm.toLowerCase();
  return options.filter(
    (o) =>
      o.label.toLowerCase().includes(term) ||
      o.country.toLowerCase().includes(term) ||
      o.value.toLowerCase().includes(term)
  );
};
